import React, { Suspense, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { Stars } from '@react-three/drei';
import AudioVisualizer from './components/AudioVisualizer';
import Controls from './components/Controls';
import AudioDebug from './components/AudioDebug';
import CameraController from './components/CameraController';
import SimpleSpectrumDisplay from './components/SimpleSpectrumDisplay';
import Instructions from './components/Instructions';
import { useVibeController } from './hooks/useVibeController';
import { useAudioAnalyzer } from './hooks/useAudioAnalyzer';
import './App.css';

function App() {
  const { vibeConfig, loading } = useVibeController();
  const { audioData, isPlaying, initializeAudio, stopAudio } = useAudioAnalyzer(vibeConfig);
  const [cameraMode, setCameraMode] = useState('orbit'); // Start with stable orbit mode

  const switchCameraMode = () => {
    const modes = ['dynamic', 'orbit', 'cinematic'];
    const currentIndex = modes.indexOf(cameraMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setCameraMode(modes[nextIndex]);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading vibe configuration...</p>
      </div>
    );
  }

  const backgroundColor = vibeConfig?.colors?.background || '#0a0a0f';

  return (
    <div className="App">
      <Instructions />
      
      <Controls 
        isPlaying={isPlaying}
        onStart={initializeAudio}
        onStop={stopAudio}
        vibeConfig={vibeConfig}
        audioData={audioData}
      />
      
      {/* Debug panel - remove this in production */}
      <AudioDebug audioData={audioData} />
      
      {/* Camera mode button */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 100
      }}>
        <button
          onClick={switchCameraMode}
          style={{
            background: 'linear-gradient(45deg, #00ffff, #ff0080)',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '25px',
            color: 'white',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
            transition: 'all 0.3s ease',
            fontFamily: 'Courier New, monospace'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 5px 25px rgba(0, 255, 255, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 0 20px rgba(0, 255, 255, 0.3)';
          }}
        >
          Camera: {cameraMode}
        </button>
      </div>
      
      {/* Camera mode info */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        background: 'rgba(0, 0, 0, 0.7)',
        color: '#00ffff',
        padding: '10px 15px',
        borderRadius: '8px',
        fontSize: '12px',
        fontFamily: 'Courier New, monospace',
        border: '1px solid rgba(0, 255, 255, 0.3)',
        zIndex: 100
      }}>
        <div>🎥 CAMERA: {cameraMode.toUpperCase()}</div>
        <div style={{ opacity: 0.7, marginTop: '5px' }}>
          Beat Detection: {audioData?.peak > 0.9 ? '🔴 BEAT' : '⚪ IDLE'}
        </div>
      </div>
      
      <Canvas
        camera={{ position: [0, 0, 12], fov: 75 }}
        style={{ background: backgroundColor }}
        gl={{ antialias: true, alpha: false }}
      >
        <Suspense fallback={null}>
          {/* Enhanced lighting for cosmic atmosphere */}
          <ambientLight intensity={0.2} color="#1a0033" />
          <pointLight position={[0, 0, 0]} intensity={2} color="#00ffff" />
          <pointLight 
            position={[10, 10, 10]} 
            intensity={audioData ? 1 + audioData.peak * 2 : 1} 
            color="#ff0080" 
          />
          <pointLight 
            position={[-10, -10, -10]} 
            intensity={audioData ? 0.8 + audioData.bass * 1.5 : 0.8} 
            color="#8000ff" 
          />
          <pointLight 
            position={[0, 15, 0]} 
            intensity={audioData ? 0.6 + audioData.treble * 2 : 0.6} 
            color="#ffff00" 
          />
          
          {/* Cosmic background */}
          <Stars 
            radius={200} 
            depth={100} 
            count={2000} 
            factor={6} 
            saturation={0.5} 
            fade 
            speed={0.5}
          />
          
          {/* Audio visualizer */}
          {audioData && vibeConfig && (
            <AudioVisualizer 
              audioData={audioData} 
              vibeConfig={vibeConfig} 
            />
          )}
          
          {/* Dynamic camera system */}
          <CameraController 
            audioData={audioData} 
            vibeConfig={vibeConfig}
            cameraMode={cameraMode}
          />
        </Suspense>
      </Canvas>
      
      {/* Simple 2D Spectrum Display */}
      <SimpleSpectrumDisplay audioData={audioData} isPlaying={isPlaying} />
    </div>
  );
}

export default App;